import anime, { EasingOptions } from 'animejs';

import { SvgSubPath, WritingPathWithSubPaths } from '../types/svg.types.ts';

const LETTER_DRAWING_ANIMATION_SPEED = 100; // in px per second
const LETTER_DRAWING_ANIMATION_EASING: EasingOptions = 'linear';

class LetterDrawingUtil {
  private static _instance: LetterDrawingUtil;
  private animationInstances: anime.AnimeInstance[] = [];

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): LetterDrawingUtil {
    if (!LetterDrawingUtil._instance) {
      LetterDrawingUtil._instance = new LetterDrawingUtil();
    }
    return LetterDrawingUtil._instance;
  }

  private isAnimating = false;

  async drawLetter(path: WritingPathWithSubPaths): Promise<void> {
    if (this.isAnimating) {
      // If already animating, pause all animations and clear the drawing
      this.animationInstances.forEach((instance) => instance.pause());
      await this.clearDrawing(path);
    }
    this.isAnimating = true;
    await this.drawLetterPart(path.subPaths);
    this.isAnimating = false;
  }

  private async drawLetterPart(subPaths: SvgSubPath[]): Promise<void> {
    for (const subPath of subPaths) {
      const element = document.getElementById(`${subPath.hash}`);
      if (element) {
        await this.animateSubPath(element, subPath);
      }
    }
  }

  private async animateSubPath(element: HTMLElement, subPath: SvgSubPath): Promise<void> {
    return new Promise<void>((resolve) => {
      anime({
        targets: element,
        strokeDashoffset: [anime.setDashoffset, 0],
        duration: this.computeDuration(subPath),
        easing: LETTER_DRAWING_ANIMATION_EASING,
        complete: () => {
          resolve();
        },
      });
    });
  }

  private computeDuration(subPath: SvgSubPath): number {
    return subPath.length / (LETTER_DRAWING_ANIMATION_SPEED / 1000);
  }

  public async clearDrawing(path: WritingPathWithSubPaths): Promise<void> {
    await Promise.all(
      path.subPaths.map(async (subPath) => {
        const element = document.getElementById(`${subPath.hash}`);
        if (element) {
          return new Promise<void>((resolve) => {
            anime({
              targets: element,
              strokeDashoffset: [0, anime.setDashoffset],
              duration: 0,
              easing: LETTER_DRAWING_ANIMATION_EASING,
              complete: () => {
                resolve();
              },
            });
          });
        }
      })
    );
  }
}

export const getLetterDrawingUtil = (): LetterDrawingUtil => LetterDrawingUtil.instance;
